import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ──────────────────────────────────────────────
// UI store — sidebar state, modals, theme, etc.
// ──────────────────────────────────────────────

interface UIState {
  /** Whether the sidebar is collapsed (desktop) */
  sidebarCollapsed: boolean;
  /** Whether the mobile sidebar/drawer is open */
  mobileSidebarOpen: boolean;
  /** Whether the "More" sheet in mobile nav is open */
  mobileMoreOpen: boolean;
  /** Whether the command palette (Cmd+K) is open */
  commandPaletteOpen: boolean;
  /** Whether the app is online */
  isOnline: boolean;
  /** Current breadcrumb segments */
  breadcrumbs: BreadcrumbItem[];

  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  setMobileMoreOpen: (open: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setOnline: (online: boolean) => void;
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      mobileMoreOpen: false,
      commandPaletteOpen: false,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      breadcrumbs: [],

      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),

      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),

      setMobileSidebarOpen: (mobileSidebarOpen) => set({ mobileSidebarOpen }),

      setMobileMoreOpen: (mobileMoreOpen) => set({ mobileMoreOpen }),

      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),

      setOnline: (isOnline) => set({ isOnline }),

      setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),
    }),
    {
      name: 'worsaas-ui',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);
